<template>
  <AppShell :hero-mode="!isLoggedIn">
    <!-- Guest hero -->
    <PageHero
      v-if="!isLoggedIn"
      eyebrow="Hall of Fame"
      icon="fas fa-trophy"
      title="Leaderboard"
      subtitle="Earn points, climb the ranks from Kiddie to Mastermind, and compete with hackers around the world."
      cta="Join & Compete"
      cta-href="/register"
      secondary="Browse Modules"
      secondary-href="/modules"
    />

    <div :class="isLoggedIn ? '' : 'container-app py-10'">

      <!-- Compact heading (authed) -->
      <div v-if="isLoggedIn" class="mb-8">
        <p class="eyebrow"><i class="fas fa-trophy"></i> Hall of Fame</p>
        <h1 class="mt-2 text-3xl font-extrabold text-white">Leaderboard</h1>
        <p class="mt-2 text-ink-muted">Track your progress and compete with hackers around the world.</p>
      </div>

      <!-- Loading state -->
      <div v-if="loading" class="flex justify-center items-center py-32">
        <div class="loader"></div>
      </div>

      <template v-else>

        <!-- ══ YOUR POSITION CARD ══ -->
        <div
          v-if="isLoggedIn && currentUserEntry"
          class="card mb-6 overflow-hidden"
        >
          <div class="flex flex-col sm:flex-row sm:items-center gap-4 px-5 py-4">
            <!-- left accent bar -->
            <div class="hidden sm:block w-1 self-stretch rounded-full bg-brand-gradient shrink-0"></div>

            <div class="flex items-center gap-4 flex-1 min-w-0">
              <!-- rank bubble -->
              <div class="flex flex-col items-center justify-center w-14 h-14 rounded-xl bg-brand/10 border border-brand/30 shrink-0">
                <span class="text-[10px] uppercase tracking-wider text-brand font-bold leading-none mb-0.5">Rank</span>
                <span class="text-xl font-extrabold text-white leading-none">#{{ currentUserEntry.rank }}</span>
              </div>

              <!-- name + tier -->
              <div class="min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="font-bold text-white text-base truncate">{{ currentUserEntry.username }}</span>
                  <span class="badge bg-brand/20 text-brand border border-brand/30">YOU</span>
                  <span
                    v-if="currentUserEntry.isSubscribed"
                    class="badge bg-yellow-500/20 text-yellow-400 border border-yellow-500/30"
                  >
                    <i class="fas fa-crown text-[10px]"></i> Premium
                  </span>
                </div>
                <p class="text-xs text-ink-muted mt-0.5">{{ getTier(currentUserEntry.points) }}</p>
              </div>
            </div>

            <!-- points + courses -->
            <div class="flex gap-6 sm:gap-8 shrink-0 pl-0 sm:pl-4">
              <div class="text-center">
                <p class="text-xl font-extrabold gradient-text">{{ currentUserEntry.points.toLocaleString() }}</p>
                <p class="text-[10px] uppercase tracking-wider text-ink-faint">Points</p>
              </div>
              <div class="text-center">
                <p class="text-xl font-extrabold text-white">{{ currentUserEntry.enrollmentCount }}</p>
                <p class="text-[10px] uppercase tracking-wider text-ink-faint">Courses</p>
              </div>
            </div>
          </div>
        </div>

        <!-- ══ PODIUM (top 3) ══ -->
        <div v-if="topThree.length" class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <!-- 1st place — centre, taller -->
          <div
            :key="topThree[0].id"
            class="card p-6 text-center relative overflow-hidden sm:order-2 border-yellow-500/30"
            style="box-shadow: 0 0 0 1px rgba(234,179,8,.2), 0 16px 50px -12px rgba(234,179,8,.18);"
          >
            <div class="absolute inset-x-0 top-0 h-1 bg-yellow-500"></div>
            <!-- crown glow behind trophy -->
            <div class="relative inline-flex items-center justify-center mb-3">
              <div class="absolute w-12 h-12 rounded-full bg-yellow-500/15 blur-lg"></div>
              <i class="fas fa-trophy text-3xl text-yellow-500 relative z-10"></i>
            </div>
            <p class="text-[10px] font-bold uppercase tracking-[0.18em] text-yellow-500/80 mb-1">1st Place</p>
            <p class="text-lg font-bold text-white truncate">{{ topThree[0].username }}</p>
            <p class="text-xs text-ink-muted mb-1">{{ getTier(topThree[0].points) }}</p>
            <div class="flex items-center justify-center gap-1.5 mt-3">
              <span class="text-2xl font-extrabold gradient-text">{{ topThree[0].points.toLocaleString() }}</span>
              <span class="text-[11px] uppercase tracking-wider text-ink-faint">pts</span>
            </div>
            <div class="mt-3 flex justify-center gap-2 flex-wrap">
              <span v-if="topThree[0].isSubscribed" class="badge bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                <i class="fas fa-crown text-[10px]"></i> Premium
              </span>
            </div>
          </div>

          <!-- 2nd place -->
          <div
            :key="topThree[1] && topThree[1].id"
            v-if="topThree[1]"
            class="card p-6 text-center relative overflow-hidden sm:order-1 border-gray-500/20"
          >
            <div class="absolute inset-x-0 top-0 h-1 bg-gray-400"></div>
            <i class="fas fa-trophy text-2xl text-gray-300 mb-3"></i>
            <p class="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-400/80 mb-1">2nd Place</p>
            <p class="text-lg font-bold text-white truncate">{{ topThree[1].username }}</p>
            <p class="text-xs text-ink-muted mb-1">{{ getTier(topThree[1].points) }}</p>
            <div class="flex items-center justify-center gap-1.5 mt-3">
              <span class="text-xl font-extrabold text-white">{{ topThree[1].points.toLocaleString() }}</span>
              <span class="text-[11px] uppercase tracking-wider text-ink-faint">pts</span>
            </div>
            <div class="mt-3 flex justify-center gap-2 flex-wrap">
              <span v-if="topThree[1].isSubscribed" class="badge bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                <i class="fas fa-crown text-[10px]"></i> Premium
              </span>
            </div>
          </div>

          <!-- 3rd place -->
          <div
            :key="topThree[2] && topThree[2].id"
            v-if="topThree[2]"
            class="card p-6 text-center relative overflow-hidden sm:order-3 border-amber-700/20"
          >
            <div class="absolute inset-x-0 top-0 h-1 bg-amber-600"></div>
            <i class="fas fa-trophy text-2xl text-amber-600 mb-3"></i>
            <p class="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-600/80 mb-1">3rd Place</p>
            <p class="text-lg font-bold text-white truncate">{{ topThree[2].username }}</p>
            <p class="text-xs text-ink-muted mb-1">{{ getTier(topThree[2].points) }}</p>
            <div class="flex items-center justify-center gap-1.5 mt-3">
              <span class="text-xl font-extrabold text-white">{{ topThree[2].points.toLocaleString() }}</span>
              <span class="text-[11px] uppercase tracking-wider text-ink-faint">pts</span>
            </div>
            <div class="mt-3 flex justify-center gap-2 flex-wrap">
              <span v-if="topThree[2].isSubscribed" class="badge bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                <i class="fas fa-crown text-[10px]"></i> Premium
              </span>
            </div>
          </div>
        </div>

        <!-- ══ FILTERS ROW ══ -->
        <div class="flex flex-col sm:flex-row gap-3 mb-5 items-start sm:items-center">
          <!-- Search -->
          <div class="relative w-full sm:max-w-xs">
            <i class="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint text-sm pointer-events-none"></i>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search username..."
              class="input pl-10"
              @input="currentPage = 1"
            />
          </div>

          <!-- Tier chips -->
          <div class="flex items-center gap-2 flex-wrap">
            <button
              v-for="tier in availableTiers"
              :key="tier"
              :class="[
                'chip transition-all duration-150 cursor-pointer',
                selectedTier === tier
                  ? 'bg-brand/20 border-brand/50 text-brand'
                  : 'hover:border-brand/30 hover:text-ink',
              ]"
              @click="selectTier(tier)"
            >
              {{ tier === '__all__' ? 'All Tiers' : tier }}
            </button>
          </div>
        </div>

        <!-- ══ TABLE ══ -->
        <div class="card overflow-hidden">
          <div class="overflow-x-auto">
            <table class="min-w-full text-sm">
              <thead>
                <tr class="text-left text-[11px] uppercase tracking-wider text-ink-faint border-b border-line">
                  <th class="px-5 py-3.5 w-16">Rank</th>
                  <th class="px-5 py-3.5">Player</th>
                  <th class="px-5 py-3.5 hidden sm:table-cell">Tier</th>
                  <th class="px-5 py-3.5">Points</th>
                  <th class="px-5 py-3.5 hidden md:table-cell">Courses</th>
                </tr>
              </thead>
              <tbody>
                <!-- Empty state when filters yield no results -->
                <tr v-if="paginatedPlayers.length === 0">
                  <td colspan="5" class="px-5 py-16 text-center">
                    <i class="fas fa-search text-ink-faint text-3xl mb-3 block"></i>
                    <p class="text-ink-muted font-medium">No players found</p>
                    <p class="text-ink-faint text-xs mt-1">Try a different search term or tier filter.</p>
                    <button class="chip mt-4 cursor-pointer hover:border-brand/30" @click="clearFilters">
                      Clear filters
                    </button>
                  </td>
                </tr>

                <tr
                  v-for="player in paginatedPlayers"
                  :key="player.id"
                  :class="[
                    'border-b border-line/60 transition-colors',
                    player.id === currentUserId
                      ? 'bg-brand/[0.07] ring-1 ring-inset ring-brand/30'
                      : 'hover:bg-surface-light/40',
                  ]"
                >
                  <!-- Rank -->
                  <td class="px-5 py-3.5">
                    <span v-if="getRealRank(player) === 1" class="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-yellow-500/15">
                      <i class="fas fa-trophy text-yellow-500 text-xs"></i>
                    </span>
                    <span v-else-if="getRealRank(player) === 2" class="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-gray-400/10">
                      <i class="fas fa-trophy text-gray-300 text-xs"></i>
                    </span>
                    <span v-else-if="getRealRank(player) === 3" class="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-amber-600/15">
                      <i class="fas fa-trophy text-amber-600 text-xs"></i>
                    </span>
                    <span v-else class="font-semibold text-ink-muted tabular-nums">#{{ getRealRank(player) }}</span>
                  </td>

                  <!-- Player -->
                  <td class="px-5 py-3.5">
                    <div class="flex items-center gap-2 flex-wrap">
                      <span class="font-semibold text-white">{{ player.username }}</span>
                      <!-- YOU badge -->
                      <span
                        v-if="player.id === currentUserId"
                        class="badge bg-brand/20 text-brand border border-brand/30"
                      >YOU</span>
                      <!-- Premium badge -->
                      <span
                        v-if="player.isSubscribed"
                        class="badge bg-yellow-500/20 text-yellow-400 border border-yellow-500/30"
                      >
                        <i class="fas fa-crown text-[10px]"></i> Premium
                      </span>
                      <!-- Tier shown inline on mobile -->
                      <span class="badge bg-surface-lighter text-ink-muted sm:hidden">
                        {{ getTier(player.points) }}
                      </span>
                    </div>
                  </td>

                  <!-- Tier (hidden on mobile, shown on sm+) -->
                  <td class="px-5 py-3.5 hidden sm:table-cell">
                    <span class="chip">{{ getTier(player.points) }}</span>
                  </td>

                  <!-- Points -->
                  <td class="px-5 py-3.5">
                    <span class="font-bold text-white tabular-nums">{{ player.points.toLocaleString() }}</span>
                    <i class="fas fa-fire text-brand ml-1.5 text-xs"></i>
                  </td>

                  <!-- Courses -->
                  <td class="px-5 py-3.5 hidden md:table-cell text-ink-muted tabular-nums">
                    {{ player.enrollmentCount }}
                    <i class="fas fa-book text-brand/60 ml-1 text-xs"></i>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ══ PAGINATION ══ -->
        <div v-if="totalPages > 1" class="flex justify-center mt-6 gap-2">
          <button class="btn-secondary" :disabled="currentPage === 1" @click="currentPage--">
            <i class="fas fa-chevron-left text-xs"></i> Previous
          </button>
          <span class="btn-ghost cursor-default">Page {{ currentPage }} of {{ totalPages }}</span>
          <button class="btn-secondary" :disabled="currentPage === totalPages" @click="currentPage++">
            Next <i class="fas fa-chevron-right text-xs"></i>
          </button>
        </div>

      </template>
    </div>
  </AppShell>
</template>

<script>
import AppShell from "@/components/ui-components/AppShell.vue";
import PageHero from "@/components/ui-components/PageHero.vue";
import apiClient from "@/axiosConfig";
import { ranks } from "@/config/ranks.js";
import { isAuthenticated } from "@/utils/auth";

export default {
  name: "LeaderboardPage",
  components: { AppShell, PageHero },
  data() {
    return {
      searchQuery: "",
      players: [],
      loading: true,
      currentPage: 1,
      itemsPerPage: 100,
      // NEW: current-user support
      currentUserId: null,
      selectedTier: "__all__",
    };
  },
  computed: {
    // ── EXISTING ──────────────────────────────────────────────
    isLoggedIn() {
      return isAuthenticated();
    },
    filteredPlayers() {
      const query = this.searchQuery.toLowerCase();
      return this.players.filter((player) => {
        const matchesSearch = player.username.toLowerCase().includes(query);
        const matchesTier =
          this.selectedTier === "__all__" || this.getTier(player.points) === this.selectedTier;
        return matchesSearch && matchesTier;
      });
    },
    paginatedPlayers() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredPlayers.slice(start, start + this.itemsPerPage);
    },
    totalPages() {
      return Math.ceil(this.filteredPlayers.length / this.itemsPerPage);
    },
    topThree() {
      return this.players.slice(0, 3);
    },

    // ── NEW ───────────────────────────────────────────────────
    // The current user's leaderboard entry with their rank position
    currentUserEntry() {
      if (!this.currentUserId) return null;
      const idx = this.players.findIndex((p) => p.id === this.currentUserId);
      if (idx === -1) return null;
      return { ...this.players[idx], rank: idx + 1 };
    },
    // Tier chips: "All" + every rank name that actually appears among players
    availableTiers() {
      const seen = new Set();
      this.players.forEach((p) => seen.add(this.getTier(p.points)));
      // Preserve the canonical rank order from ranks.js
      const ordered = ranks
        .map((r) => r.name)
        .filter((name) => seen.has(name));
      return ["__all__", ...ordered];
    },
  },
  methods: {
    // ── EXISTING ──────────────────────────────────────────────
    async fetchLeaderboard() {
      try {
        const response = await apiClient("/scoreboard");
        this.players = response.data.scoreboard;
      } catch (error) {
        console.error("Error fetching leaderboard data:", error);
      } finally {
        this.loading = false;
      }
    },
    getRealRank(player) {
      return this.players.findIndex((p) => p.id === player.id) + 1;
    },
    getTier(points) {
      const tier = ranks.find((rank) => points >= rank.min && points <= rank.max);
      return tier ? tier.name : "Unknown";
    },
    trophyColor(i) {
      return ["text-yellow-500", "text-gray-300", "text-amber-600"][i];
    },
    podiumBar(i) {
      return ["bg-yellow-500", "bg-gray-400", "bg-amber-600"][i];
    },

    // ── NEW ───────────────────────────────────────────────────
    async fetchCurrentUser() {
      if (!isAuthenticated()) return;
      try {
        const response = await apiClient.get("/users/current");
        this.currentUserId = response.data.user.id;
      } catch (error) {
        console.error("Error fetching current user:", error);
      }
    },
    selectTier(tier) {
      this.selectedTier = tier;
      this.currentPage = 1;
    },
    clearFilters() {
      this.searchQuery = "";
      this.selectedTier = "__all__";
      this.currentPage = 1;
    },
  },
  mounted() {
    this.fetchLeaderboard();
    this.fetchCurrentUser();
  },
};
</script>

<style scoped>
.loader {
  border: 4px solid rgba(255, 255, 255, 0.08);
  border-top: 4px solid #ff3131;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
